import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { DateTime } from 'luxon';
import { emitAppNotification } from '../../services/appNotifications';
import {
  dismissReminder,
  listReminderAlerts,
  listReminders,
  snoozeReminder,
} from '../../services/reminders/remindersApi';
import { isRemindersEnabledForUser } from '../../utils/remindersEntitlement';
import { useAuth } from '../../contexts/AuthContext';

const LOCAL_TIMEOUT_HORIZON_MS = 24 * 60 * 60 * 1000;
const REST_POLL_MS = 10 * 1000;

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.08;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
  } catch (_) {
    /* non-blocking */
  }
}

function localAlertFromReminder(item) {
  const occ = item.nextOccurrenceId;
  return {
    id: `local-${occ}`,
    local: true,
    occurrenceId: occ,
    reminderId: item.id,
    title: item.title || 'Reminder',
    message: item.message || item.title,
    firedAt: item.nextFireAt || item.dueAt,
    flowError: null,
  };
}

async function ensureWebNotificationPermission() {
  try {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  } catch (_) {
    /* browser may block until a later user gesture */
  }
}

export function dueWaitMs(dueIso, nowMs = Date.now()) {
  const dueMs = DateTime.fromISO(dueIso).toMillis();
  if (!Number.isFinite(dueMs)) return null;
  return dueMs - nowMs;
}

export default function ReminderAlertHost() {
  const { userData, tenantId, currentUser } = useAuth() || {};
  const entitled = isRemindersEnabledForUser(userData);
  const [queue, setQueue] = useState([]);
  const seenRef = useRef(new Set());
  const seenOccRef = useRef(new Set());
  const dismissedOccRef = useRef(new Set());
  const timersRef = useRef(new Map());

  const enqueue = useCallback((alert) => {
    if (!alert) return;
    const occ = alert.occurrenceId;
    const id = alert.id;
    if (occ && dismissedOccRef.current.has(occ)) {
      if (id && !alert.local) {
        dismissReminder(alert.reminderId, id).catch(() => {});
      }
      return;
    }
    if (occ && seenOccRef.current.has(occ)) return;
    if (id && seenRef.current.has(id)) return;
    if (occ) seenOccRef.current.add(occ);
    if (id) seenRef.current.add(id);
    setQueue((prev) => [...prev, alert]);
    const title = alert.title || 'Reminder';
    const message = alert.message || title;
    emitAppNotification({
      id: `reminder-alert-${id || occ}`,
      severity: 'warning',
      source: 'reminder',
      type: 'status',
      visibility: 'when_minimized',
      context: {
        title,
        message,
        meta: {
          title,
          message,
          reminderId: alert.reminderId,
          alertId: id,
          occurrenceId: occ,
          visibility: 'when_minimized',
        },
      },
    }, { skipOSNotification: false, skipInApp: true });
    playChime();
  }, []);

  const loadPending = useCallback(async () => {
    if (!entitled) return;
    try {
      const alerts = await listReminderAlerts(false);
      (alerts || []).forEach(enqueue);
    } catch (_) {
      /* offline until next refresh */
    }
  }, [entitled, enqueue]);

  const clearTimer = useCallback((reminderId) => {
    const handle = timersRef.current.get(reminderId);
    if (handle) {
      clearTimeout(handle);
      timersRef.current.delete(reminderId);
    }
  }, []);

  const armItems = useCallback((items) => {
    const live = new Set();
    (items || []).forEach((item) => {
      if (!item?.id) return;
      live.add(item.id);
      clearTimer(item.id);
      const occ = item.nextOccurrenceId;
      const dueIso = item.nextFireAt || item.dueAt;
      if (item.status !== 'scheduled' || !occ || !dueIso) return;
      if (seenOccRef.current.has(occ) || dismissedOccRef.current.has(occ)) return;
      const wait = dueWaitMs(dueIso);
      if (wait == null || wait > LOCAL_TIMEOUT_HORIZON_MS) return;
      if (wait <= 0) {
        enqueue(localAlertFromReminder(item));
        return;
      }
      timersRef.current.set(item.id, setTimeout(() => {
        enqueue(localAlertFromReminder(item));
      }, wait));
    });
    Array.from(timersRef.current.keys()).forEach((id) => {
      if (!live.has(id)) clearTimer(id);
    });
  }, [clearTimer, enqueue]);

  const syncFromApi = useCallback(async () => {
    if (!entitled) return;
    try {
      const lists = await listReminders('all');
      armItems([...(lists.active || []), ...(lists.upcoming || [])]);
    } catch (_) {
      /* Firestore listener or next poll remains */
    }
  }, [entitled, armItems]);

  useEffect(() => {
    if (!entitled) return undefined;
    ensureWebNotificationPermission();
    loadPending();
    syncFromApi();
    const poll = setInterval(() => {
      loadPending();
      syncFromApi();
    }, REST_POLL_MS);
    const onFocus = () => {
      loadPending();
      syncFromApi();
    };
    const onChanged = () => {
      loadPending();
      syncFromApi();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('signalbridge:reminders-changed', onChanged);
    return () => {
      clearInterval(poll);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('signalbridge:reminders-changed', onChanged);
    };
  }, [entitled, loadPending, syncFromApi]);

  useEffect(() => {
    if (!entitled || !tenantId || !currentUser?.uid) return undefined;
    let unsub = () => {};
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'tenants', tenantId, 'reminder_alerts'),
        where('ownerUserId', '==', currentUser.uid)
      );
      unsub = onSnapshot(q, (snap) => {
        snap.docChanges().forEach((change) => {
          const data = { id: change.doc.id, ...(change.doc.data() || {}) };
          if (data.dismissed) return;
          if (change.type === 'added' || change.type === 'modified') enqueue(data);
        });
      }, () => {});
    } catch (_) {
      /* REST fallback remains */
    }
    return () => {
      try { unsub(); } catch (_) { /* ignore */ }
    };
  }, [entitled, tenantId, currentUser?.uid, enqueue]);

  useEffect(() => {
    if (!entitled || !tenantId || !currentUser?.uid) return undefined;
    let unsub = () => {};
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'tenants', tenantId, 'reminders'),
        where('ownerUserId', '==', currentUser.uid)
      );
      unsub = onSnapshot(q, (snap) => {
        armItems(snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) })));
      }, () => {});
    } catch (_) {
      /* REST poll remains the clock */
    }
    return () => {
      try { unsub(); } catch (_) { /* ignore */ }
    };
  }, [entitled, tenantId, currentUser?.uid, armItems]);

  useEffect(() => () => {
    timersRef.current.forEach((handle) => clearTimeout(handle));
    timersRef.current.clear();
  }, []);

  const current = queue[0];
  if (!current) return null;

  const when = current.firedAt
    ? DateTime.fromISO(current.firedAt).toFormat("ccc LLL d 'at' h:mm a")
    : '';

  return (
    <Dialog open fullWidth maxWidth="xs" aria-labelledby="reminder-alert-title">
      <DialogTitle id="reminder-alert-title">{current.title || 'Reminder'}</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Typography>{current.message || current.title}</Typography>
          {when && <Typography variant="body2" color="text.secondary">Fired {when}</Typography>}
          {current.flowError && (
            <Typography variant="body2" color="error">
              In-app alert was delivered. Flow failed: {current.flowError}
            </Typography>
          )}
          {queue.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              {queue.length - 1} more waiting
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={async () => {
          if (current.occurrenceId) dismissedOccRef.current.add(current.occurrenceId);
          try { await snoozeReminder(current.reminderId, 300); } catch (_) { /* keep queue moving */ }
          notifyAfterAction();
          setQueue((prev) => prev.slice(1));
        }}>
          Snooze 5m
        </Button>
        <Button variant="contained" onClick={async () => {
          if (current.occurrenceId) dismissedOccRef.current.add(current.occurrenceId);
          try {
            if (!current.local) await dismissReminder(current.reminderId, current.id);
          } catch (_) { /* keep queue moving */ }
          notifyAfterAction();
          setQueue((prev) => prev.slice(1));
        }}>
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function notifyAfterAction() {
  try {
    window.dispatchEvent(new CustomEvent('signalbridge:reminders-changed'));
  } catch (_) {
    /* ignore */
  }
}
